import { languageManager, type Language } from './languageManager';

export interface I18nText {
    en: string;
    id: string;
    ko: string;
    vn: string;
}

export type I18nMap = { [key: string]: Partial<I18nText> };

/**
 * Translation object type: { [languageCode]: { [englishSentence]: 'sentence in language' } }
 */
export type TranslationObject = { [lang in Language]?: { [englishKey: string]: string } };

/**
 * Creates a translation function for a given set of translations.
 * Pre-compiles all language versions at creation time for O(1) lookup performance.
 * Automatically uses the current language from languageManager.
 */
export function createI18n(translations: I18nMap) {
    // Pre-compile all translations for all languages
    const compiled: Record<Language, Record<string, string>> = {
        en: {},
        id: {},
        ko: {},
        vn: {}
    };

    // Build lookup tables for each language
    for (const [key, translation] of Object.entries(translations)) {
        compiled.en[key] = translation?.en || key;
        compiled.id[key] = translation?.id || translation?.en || key;
        compiled.ko[key] = translation?.ko || translation?.en || key;
        compiled.vn[key] = translation?.vn || translation?.en || key;
    }

    // Return translation function that automatically uses current language
    return (key: string): string => {
        const lang = languageManager.getLanguage();
        return compiled[lang]?.[key] || compiled.en[key] || key;
    };
}

/**
 * tLoader: Creates a translation function from a translation object.
 *
 * Translation object format: { [languageCode]: { [englishSentence]: 'sentence in language' } }
 *
 * @example
 * const translations = {
 *     en: { "Hello": "Hello", "Welcome": "Welcome" },
 *     id: { "Hello": "Halo", "Welcome": "Selamat datang" },
 *     ko: { "Hello": "안녕하세요", "Welcome": "환영합니다" },
 *     vn: { "Hello": "Xin chào", "Welcome": "Chào mừng" }
 * };
 * const t = tLoader(translations);
 * t("Hello") // Returns translation based on current language
 *
 * @param translations - Translation object with language codes as keys
 * @returns Translation function (englishSentence) => 'sentence in language'
 */
export function tLoader(translations: TranslationObject): (key: string) => string {
    return (key: string): string => {
        const lang = languageManager.getLanguage();
        return translations[lang]?.[key] || translations.en?.[key] || key;
    };
}

/**
 * Hook-friendly version that returns both the t function and current language.
 * Useful when you need to re-render on language changes.
 */
export function useTLoader(translations: TranslationObject): { t: (key: string) => string; lang: Language } {
    const lang = languageManager.getLanguage();
    const t = (key: string): string => {
        return translations[lang]?.[key] || translations.en?.[key] || key;
    };
    return { t, lang };
}